import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { completeDirectorKyc, updateDirectorKycStatus } from "../../actions/user";
import Logo from "../../assets/img/logo.png";
import { FiCheckCircle, FiShield, FiAlertTriangle, FiUserCheck } from "react-icons/fi";

const BusinessDirectorKyc = () => {
  const { kycVerificationReference } = useParams();
  const [loading, setLoading] = useState(true);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [kycStatus, setKycStatus] = useState("initializing"); // 'idle' | 'processing' | 'success' | 'failed'
  const [errorMsg, setErrorMsg] = useState("");
  const [kycVerificationDetails, setKycVerificationDetails] = useState(null);
  
  /** Load Dojah SDK dynamically */
  useEffect(() => {
    const scriptId = "dojah-sdk-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://widget.dojah.io/websdk.js";
      script.defer = true;
      script.onload = () => setSdkLoaded(true);
      document.body.appendChild(script);
    } else {
      setSdkLoaded(true);
    }
  }, []);

  const openDojahWidget = (data) => {
    if (!sdkLoaded || !window.Connect) {
      toast.error("Verification engine is loading, please try again in a few seconds.");
      return;
    }

    try {
      const options = {
        app_id: data.widget_config.app_id || data.widget_config.app_id_key,
        p_key: data.widget_config.public_key || data.widget_config.p_key,
        type: "custom",
        metadata: {
          kyc_verification_id: data.kyc_verification_record?.reference_id || kycVerificationReference,
          kyc_verification_type: 'business_director_kyc',
        },
        config: { 
          widget_id: data.widget_config.widget_id, 
          webhook: true 
        },
        onSuccess: async (response) => {
          try {
            const formData = new FormData();
            formData.append("dojah_kyc_reference", response?.referenceId || response?.reference || "");
            await updateDirectorKycStatus(data.kyc_verification_record?.reference_id || kycVerificationReference, formData);
            setKycStatus("success");
            toast.success("KYC Verification completed successfully!");
          } catch (err) {
            console.error("Failed to update KYC status:", err);
            setKycStatus("failed");
            setErrorMsg("Failed to update verification status. Please try again.");
          }
        },
        onError: (err) => {
          console.error("Dojah Widget Error:", err);
          toast.error("Verification process encountered an error.");
          setKycStatus("failed");
          setErrorMsg("Dojah verification failed. Please try again.");
        },
        onClose: () => {
          setKycStatus("idle");
        },
      };

      const connect = new window.Connect(options);
      connect.setup();
      connect.open();
    } catch (err) {
      console.error("Dojah initialization error:", err);
      toast.error("Failed to load Dojah Connect widget.");
    }
  };

  const handleStartKyc = async () => {
    setLoading(true);
    setKycStatus("processing");
    try {
      const response = await completeDirectorKyc(kycVerificationReference);
      // Check if Dojah connects options are returned
      if (kycVerificationDetails.kyc_verification_record.provider_name == 'dojah') {
        openDojahWidget(kycVerificationDetails);
      } 
      else if (kycVerificationDetails.redirect_url) {
        // Redirection support
        window.location.href = kycVerificationDetails.redirect_url;
      } else {
        setKycStatus("success");
        toast.success(response?.message || "KYC verification processed successfully.");
      }
    } catch (err) {
      setErrorMsg("A technical error ocurred. Please try again. If error persists, kindly reach out to our support team.");
      setKycStatus("failed");
    } finally {
      setLoading(false);
    }
  };

  const initializeKyc = async () => {
    try {
      const response = await completeDirectorKyc(kycVerificationReference);
      // Check if Dojah connects options are returned
      if (response?.status === "success"){
        console.log(response?.data);
        setKycVerificationDetails(response?.data)
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to initiate verification.";
      setErrorMsg(msg);
    } finally {
      setLoading(false);
      setKycStatus("idle");
    }
  }

  console.log('KYc verification', kycVerificationDetails);

  useEffect(() => {
    initializeKyc();
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <img src={Logo} alt="Cardplot Logo" style={styles.logo} />
        </div>

        {kycStatus === "idle" && kycVerificationDetails && (
          <div style={styles.content}>
            <div style={styles.iconContainer}>
              <FiShield style={styles.iconPrimary} />
            </div>
            <h4 style={styles.title}>Director Verification</h4>
            <p style={styles.text}>
              As a Business Director or Owner, we require quick identity verification to securely complete onboarding for your business.
            </p>
            
            <button
              onClick={handleStartKyc}
              disabled={loading}
              style={styles.button}
            >
              {loading ? "Initializing..." : "Start Secure KYC"}
            </button>
          </div>
        )}

        {kycStatus === "idle" && errorMsg && (
          <div style={styles.content}>
            <div style={styles.iconContainer}>
              <FiShield style={styles.iconPrimary} />
            </div>
            <h4 style={styles.title}>An Error Ocurred</h4>
            <p style={styles.text}>
              {errorMsg}
            </p>
          </div>
        )}

        {kycStatus === "initializing" && (
          <div style={styles.content}>
            <div style={styles.spinner} />
          </div>
        )}

        {kycStatus === "processing" && (
          <div style={styles.content}>
            <div style={styles.spinner} />
            <h4 style={styles.title}>Initializing Verification</h4>
            <p style={styles.text}>
              Please wait while we set up your secure verification session...
            </p>
          </div>
        )}

        {kycStatus === "success" && (
          <div style={styles.content}>
            <div style={styles.iconContainerSuccess}>
              <FiCheckCircle style={styles.iconSuccess} />
            </div>
            <h4 style={styles.titleSuccess}>Verification Complete!</h4>
            <p style={styles.text}>
              Your identity has been successfully verified. Your business KYB application is now one step closer to approval.
            </p>
            <p style={styles.textMuted}>
              You can safely close this page. The business owner will be notified of your completed verification.
            </p>
            <div style={styles.successBox}>
              <FiUserCheck style={{ marginRight: "10px", fontSize: "1.1rem" }} />
              <span>Director KYC verified successfully</span>
            </div>
          </div>
        )}

        {kycStatus === "failed" && (
          <div style={styles.content}>
            <div style={styles.iconContainerFailed}>
              <FiAlertTriangle style={styles.iconFailed} />
            </div>
            <h4 style={styles.titleFailed}>Verification Failed</h4>
            <p style={styles.text}>
              {errorMsg || "We were unable to complete your identity verification. Please try again."}
            </p>
            <button
              onClick={() => {
                setKycStatus("idle");
                setErrorMsg("");
              }}
              style={styles.buttonRetry}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
      <div style={styles.footer}>
        <span>&copy; {new Date().getFullYear()} Cardplot Technologies. All rights reserved.</span>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #13053A 0%, #12003A 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    fontFamily: "'Inter', sans-serif",
    color: "#fff",
  },
  card: {
    width: "min(500px, 100%)",
    background: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "20px",
    padding: "40px 30px",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
    textAlign: "center",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
  },
  header: {
    marginBottom: "35px",
    display: "flex",
    justifyContent: "center",
  },
  logo: {
    height: "38px",
    objectFit: "contain",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  iconContainer: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    background: "rgba(26, 37, 80, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  },
  iconPrimary: {
    fontSize: "2rem",
    color: "#4A90E2",
  },
  iconContainerSuccess: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    background: "rgba(22, 219, 101, 0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
    border: "1px solid rgba(22, 219, 101, 0.3)",
  },
  iconSuccess: {
    fontSize: "2rem",
    color: "#16DB65",
  },
  iconContainerFailed: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    background: "rgba(255, 74, 74, 0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
    border: "1px solid rgba(255, 74, 74, 0.3)",
  },
  iconFailed: {
    fontSize: "2rem",
    color: "#FF4A4A",
  },
  title: {
    fontSize: "24px",
    fontWeight: "600",
    marginBottom: "15px",
    letterSpacing: "-0.5px",
  },
  titleSuccess: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#16DB65",
    marginBottom: "15px",
    letterSpacing: "-0.5px",
  },
  titleFailed: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#FF4A4A",
    marginBottom: "15px",
    letterSpacing: "-0.5px",
  },
  text: {
    fontSize: "15px",
    lineHeight: "1.6",
    color: "#CBD5E1",
    marginBottom: "15px",
  },
  textMuted: {
    fontSize: "13px",
    lineHeight: "1.5",
    color: "#94A3B8",
    marginBottom: "30px",
  },
  button: {
    width: "100%",
    padding: "14px 20px",
    background: "#4A90E2",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background 0.3s ease, transform 0.2s ease",
    boxShadow: "0 4px 12px rgba(74, 144, 226, 0.3)",
  },
  buttonRetry: {
    width: "100%",
    padding: "14px 20px",
    background: "transparent",
    color: "#FF4A4A",
    border: "2px solid #FF4A4A",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background 0.3s ease",
    marginTop: "20px",
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "3px solid rgba(255, 255, 255, 0.1)",
    borderTopColor: "#4A90E2",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "25px",
  },
  successBox: {
    display: "flex",
    alignItems: "center",
    background: "rgba(22, 219, 101, 0.1)",
    border: "1px solid rgba(22, 219, 101, 0.2)",
    borderRadius: "10px",
    padding: "12px 20px",
    color: "#16DB65",
    fontSize: "14px",
    fontWeight: "500",
    marginTop: "20px",
  },
  footer: {
    marginTop: "30px",
    fontSize: "12px",
    color: "#64748B",
    textAlign: "center",
  },
};

// Add standard spinner animation to document style
const styleElement = document.createElement("style");
styleElement.innerHTML = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleElement);

export default BusinessDirectorKyc;
